import type { MailboxDto } from "@bafe/api-client";
import { OPERATIONAL_DEFAULTS } from "@bafe/config";
import {
	Button,
	Card,
	EmptyState,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Section,
	Select,
	SkeletonRows,
	useToast,
} from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ErrorPanel } from "../components/ErrorPanel";
import { LinkButton } from "../components/LinkButton";
import { PasswordField } from "../components/PasswordField";
import { api } from "../lib/api";
import { queryKeys } from "../lib/query";

const CREDENTIAL_TTL_HOURS = OPERATIONAL_DEFAULTS.migration_credential_ttl_hours;

type Security = "ssl" | "starttls";

interface ProviderPreset {
	id: string;
	label: string;
	host: string;
	port: number;
	security: Security;
	note?: string;
}

/** Only encrypted connections are offered; there is no plaintext option. */
const PRESETS: ProviderPreset[] = [
	{
		id: "gmail",
		label: "Gmail or Google Workspace",
		host: "imap.gmail.com",
		port: 993,
		security: "ssl",
		note: "Google requires an app password rather than your normal password. Create one in your Google account security settings.",
	},
	{
		id: "microsoft",
		label: "Outlook.com or Microsoft 365",
		host: "outlook.office365.com",
		port: 993,
		security: "ssl",
		note: "If your account uses multi-factor authentication, create an app password in Microsoft security settings.",
	},
	{
		id: "yahoo",
		label: "Yahoo Mail",
		host: "imap.mail.yahoo.com",
		port: 993,
		security: "ssl",
		note: "Yahoo requires an app password generated in your account security settings.",
	},
	{
		id: "zoho",
		label: "Zoho Mail",
		host: "imap.zoho.com",
		port: 993,
		security: "ssl",
	},
	{
		id: "cpanel",
		label: "cPanel or another host",
		host: "",
		port: 993,
		security: "ssl",
		note: "Your current host publishes these settings, usually as mail.yourdomain.com on port 993.",
	},
];

export function MigrationNew() {
	const mailboxes = useQuery({
		queryKey: queryKeys.mailboxes(),
		queryFn: () => api.listMailboxes({ limit: 200 }),
	});

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				title="Set up a migration"
				description="Key connects to your old provider with IMAP and copies everything across."
				breadcrumb={
					<Link
						to="/manage/email/migration"
						className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
					>
						Migration
					</Link>
				}
			/>

			{mailboxes.isPending && <SkeletonRows rows={4} />}
			{mailboxes.isError && (
				<ErrorPanel
					error={mailboxes.error}
					title="We could not load your mailboxes."
					onRetry={() => void mailboxes.refetch()}
					retryPending={mailboxes.isFetching}
				/>
			)}
			{mailboxes.isSuccess && <NewMigration mailboxes={mailboxes.data.data} />}

			<CutoverGuidance />
		</div>
	);
}

function NewMigration({ mailboxes }: { mailboxes: MailboxDto[] }) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { toast } = useToast();

	const [mailboxId, setMailboxId] = useState("");
	const [presetId, setPresetId] = useState(PRESETS[0]?.id ?? "");
	const [host, setHost] = useState(PRESETS[0]?.host ?? "");
	const [port, setPort] = useState(String(PRESETS[0]?.port ?? 993));
	const [security, setSecurity] = useState<Security>(PRESETS[0]?.security ?? "ssl");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	const preset = PRESETS.find((item) => item.id === presetId) ?? null;
	const effectiveMailboxId = mailboxId || (mailboxes[0]?.id ?? "");

	const test = useMutation({
		mutationFn: () =>
			api.testMigrationSource({
				sourceHost: host.trim(),
				sourcePort: Number(port),
				sourceSecurity: security,
				sourceUsername: username.trim(),
				sourcePassword: password,
			}),
	});

	const startMigration = useMutation({
		mutationFn: async () => {
			const created = await api.createMigration({
				mailboxId: effectiveMailboxId,
				sourceHost: host.trim(),
				sourcePort: Number(port),
				sourceSecurity: security,
				sourceUsername: username.trim(),
				sourcePassword: password,
			});
			return api.startMigration(created.id);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.migrations });
			setPassword("");
			test.reset();
			toast({
				tone: "success",
				message: "We are moving your existing messages. You can leave this page.",
			});
			navigate("/manage/email/migration");
		},
	});

	function applyPreset(id: string) {
		setPresetId(id);
		const next = PRESETS.find((item) => item.id === id);
		if (!next) return;
		setHost(next.host);
		setPort(String(next.port));
		setSecurity(next.security);
	}

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		test.mutate();
	}

	const canTest =
		host.trim().length > 0 && username.trim().length > 0 && password.length > 0 && Number(port) > 0;
	// Connection test is advisory until the worker probes IMAP; do not block start on reachable.
	const canStart = canTest && effectiveMailboxId !== "";

	if (mailboxes.length === 0) {
		return (
			<Card>
				<EmptyState
					title="Create a mailbox first"
					description="A migration needs somewhere to put your messages. Create the Key mailbox that should receive them."
					action={
						<LinkButton to="/manage/email/mailboxes/new" variant="primary">
							Create a mailbox
						</LinkButton>
					}
				/>
			</Card>
		);
	}

	return (
		<form className="flex max-w-2xl flex-col gap-5" onSubmit={submit} noValidate>
			<Field label="Copy messages into" required>
				{({ id }) => (
					<Select
						id={id}
						value={effectiveMailboxId}
						onChange={(event) => setMailboxId(event.target.value)}
					>
						{mailboxes.map((mailbox) => (
							<option key={mailbox.id} value={mailbox.id}>
								{mailbox.emailAddress}
							</option>
						))}
					</Select>
				)}
			</Field>

			<Field label="Where is your email now?" required>
				{({ id }) => (
					<Select id={id} value={presetId} onChange={(event) => applyPreset(event.target.value)}>
						{PRESETS.map((item) => (
							<option key={item.id} value={item.id}>
								{item.label}
							</option>
						))}
					</Select>
				)}
			</Field>

			{preset?.note && <InlineNotice tone="info">{preset.note}</InlineNotice>}

			<div className="grid gap-4 sm:grid-cols-[1fr_7rem_10rem]">
				<Field label="IMAP server" required>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={host}
							required
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							placeholder="imap.example.com"
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setHost(event.target.value)}
						/>
					)}
				</Field>

				<Field label="Port" required>
					{({ id }) => (
						<Input
							id={id}
							type="number"
							inputMode="numeric"
							value={port}
							required
							onChange={(event) => setPort(event.target.value)}
						/>
					)}
				</Field>

				<Field label="Security" required>
					{({ id }) => (
						<Select
							id={id}
							value={security}
							onChange={(event) =>
								setSecurity(event.target.value === "starttls" ? "starttls" : "ssl")
							}
						>
							<option value="ssl">SSL/TLS</option>
							<option value="starttls">STARTTLS</option>
						</Select>
					)}
				</Field>
			</div>

			<Field label="Username at your old provider" help="Usually the full email address." required>
				{({ id, describedBy, invalid }) => (
					<Input
						id={id}
						value={username}
						required
						autoComplete="off"
						autoCapitalize="none"
						spellCheck={false}
						aria-describedby={describedBy}
						aria-invalid={invalid}
						onChange={(event) => setUsername(event.target.value)}
					/>
				)}
			</Field>

			<PasswordField
				label="Password at your old provider"
				value={password}
				onChange={setPassword}
				autoComplete="off"
				required
			/>

			<InlineNotice tone="info" title="This password is temporary">
				Key holds it only for as long as the migration runs, and for at most {CREDENTIAL_TTL_HOURS}{" "}
				hours. It is destroyed when the migration finishes, fails or is cancelled, and it is never
				shown again.
			</InlineNotice>

			{test.isError && <ErrorPanel error={test.error} title="We could not test that connection." />}

			{test.data && (
				<InlineNotice
					tone={test.data.reachable ? "positive" : "caution"}
					title={test.data.reachable ? "Connection works" : "We could not connect"}
				>
					<div className="flex flex-col gap-1">
						<p>{test.data.message}</p>
						{test.data.estimatedMessages !== null && (
							<p>About {test.data.estimatedMessages.toLocaleString()} messages to copy.</p>
						)}
						{test.data.folders && test.data.folders.length > 0 && (
							<p>
								{test.data.folders.length} folders found:{" "}
								{test.data.folders
									.slice(0, 6)
									.map((folder) => `${folder.name} (${folder.messageCount})`)
									.join(", ")}
								{test.data.folders.length > 6 ? "…" : ""}
							</p>
						)}
					</div>
				</InlineNotice>
			)}

			{startMigration.isError && (
				<ErrorPanel error={startMigration.error} title="We could not start this migration." />
			)}

			<div className="flex flex-wrap gap-2">
				<Button
					type="submit"
					variant="secondary"
					disabled={!canTest}
					pending={test.isPending}
					pendingLabel="Testing…"
				>
					Test connection
				</Button>
				<Button
					variant="primary"
					disabled={!canStart}
					pending={startMigration.isPending}
					pendingLabel="Starting…"
					onClick={() => startMigration.mutate()}
				>
					Start migration
				</Button>
			</div>

			{!canStart && (
				<p className="text-xs text-(--text-muted)">
					Choose a mailbox and enter your old provider details. Testing is optional; Key checks again when
					the migration runs.
				</p>
			)}
		</form>
	);
}

function CutoverGuidance() {
	const steps = [
		"Start the migration while your old email is still running. Nothing is switched off.",
		"Add Key's DNS records so new mail starts arriving here.",
		"Run a final sync once the change has settled, to pick up anything that arrived in between.",
		"Send and receive a test message to confirm new mail reaches Key.",
		"Keep your old service running a little longer, until you are confident nothing is missing.",
	];

	return (
		<Section
			title="How to switch over safely"
			description="The order matters. Following it means you never have a gap where mail could be lost."
		>
			<Card>
				<ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-(--text-secondary)">
					{steps.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ol>
			</Card>

			<InlineNotice tone="caution" title="Do not cancel your old service yet">
				Wait until you have verified that new mail arrives in Key and your old messages are all here.
				Cancelling early can make old mail unrecoverable.
			</InlineNotice>
		</Section>
	);
}
